import BugsnagPerformance from '@bugsnag/browser-performance';
import axios from 'axios';
import CryptoJS from 'crypto-js';
import {
  WavelightMode,
  WorkflowMeasurements,
  WorkflowPerson,
  WorkflowStudy,
} from '../../types';
import { getAuthToken } from '../../utils';
import {
  WorkflowMetaData,
  WorkflowVideoUploadRequest,
  WorkflowVideoUploadResponse,
} from './types';
import WAVELIGHT_URL from './url';

export const startWorkflow = async (mode: WavelightMode) => {
  const token = getAuthToken();
  const response = await axios.post(
    `${WAVELIGHT_URL}/start/`,
    { mode },
    {
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
    },
  );
  return response.data;
};

const calculateChecksum = (fileBlob: File) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();

    reader.onload = () => {
      if (reader.result instanceof ArrayBuffer) {
        const wordArray = CryptoJS.lib.WordArray.create(reader.result);
        const hash = CryptoJS.SHA256(wordArray).toString(CryptoJS.enc.Hex);
        resolve(hash);
      } else {
        reject(new Error('Failed to read file'));
      }
    };

    reader.onerror = () => {
      reject(new Error('Failed to read file'));
    };

    reader.readAsArrayBuffer(fileBlob);
  });
};

export const uploadVideoNewRoute = async (
  id: string,
  fileBlob: File,
  timeout: number,
) => {
  const timeoutInMili = timeout * 1000;
  const span = BugsnagPerformance.startSpan('UploadVideo');
  const token = getAuthToken();
  const checksum = await calculateChecksum(fileBlob);

  const formData = new FormData();
  formData.append('file', fileBlob);

  const response = await axios.put(
    `${WAVELIGHT_URL}/video-upload/${id}`,
    formData,
    {
      headers: {
        'Content-Type': 'multipart/form-data',
        Authorization: `Bearer ${token}`,
        checksum: checksum as string,
      },
      timeout: timeoutInMili,
      timeoutErrorMessage:
        'O upload demorou muito para ser concluído. Verifique sua conexão e tente novamente.',
    },
  );
  span.end();
  return response.data;
};

export const uploadHashInVideo = async (id: string, fileBlob: File) => {
  const token = getAuthToken();
  const checksum = await calculateChecksum(fileBlob);

  const response = await axios.put(
    `${WAVELIGHT_URL}/checksum/${id}`,
    {},
    {
      headers: {
        Authorization: `Bearer ${token}`,
        checksum: checksum as string,
      },
    },
  );

  return response.data;
};

export const testSpeedUpload = async (id: string, fileBlob: File) => {
  const token = getAuthToken();
  const formData = new FormData();
  formData.append('file', fileBlob);

  const response = await axios.put(
    `${WAVELIGHT_URL}/video-upload/testUpload/${id}`,
    formData,
    {
      headers: {
        'Content-Type': 'multipart/form-data',
        Authorization: `Bearer ${token}`,
      },
    },
  );
  return response.data;
};

export const getPreSignedURL = async (
  id: string,
  fileUpload: WorkflowVideoUploadRequest,
) => {
  const token = getAuthToken();
  const response = await axios.put(
    `${WAVELIGHT_URL}/video-upload/request/${id}/`,
    fileUpload,
    {
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
    },
  );
  return response.data;
};

export const uploadFileOn = async (
  presigned: WorkflowVideoUploadResponse,
  fileBlob: File,
  timeout: number,
) => {
  const timeoutInMili = timeout * 1000 + 10000;
  const span = BugsnagPerformance.startSpan('UploadVideo');
  const response = await axios.put(presigned.url, fileBlob, {
    headers: {
      'Content-Type': 'application/octet-stream',
    },
    timeout: timeoutInMili,
    timeoutErrorMessage:
      'O upload demorou muito para ser concluído. Verifique sua conexão e tente novamente.',
  });
  span.end();
  return response.data;
};

export const confirmVideoUpload = async (id: string) => {
  const token = getAuthToken();
  const response = await axios.put(
    `${WAVELIGHT_URL}/video-upload/confirm/${id}/`,
    {},
    {
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
    },
  );
  return response.data;
};

export const updatePersonData = async (id: string, data: WorkflowPerson) => {
  const token = getAuthToken();
  const response = await axios.put(
    `${WAVELIGHT_URL}/set-person-data/${id}/`,
    data,
    {
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
    },
  );
  return response.data;
};

export const updateStudyData = async (id: string, data: WorkflowStudy) => {
  const token = getAuthToken();
  const response = await axios.put(
    `${WAVELIGHT_URL}/set-study-data/${id}/`,
    data,
    {
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
    },
  );
  return response.data;
};

export const updateMeasurements = async (
  id: string,
  data: WorkflowMeasurements,
) => {
  const token = getAuthToken();
  const response = await axios.put(
    `${WAVELIGHT_URL}/set-measurement-data/${id}/`,
    data,
    {
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
    },
  );
  return response.data;
};

export const updateMetaData = async (id: string, data: WorkflowMetaData) => {
  const token = getAuthToken();
  const response = await axios.put(
    `${WAVELIGHT_URL}/set-metadata/${id}/`,
    data,
    {
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
    },
  );
  return response.data;
};

export const finishWorkflow = async (id: string) => {
  const token = getAuthToken();
  const response = await axios.put(
    `${WAVELIGHT_URL}/finish/${id}/`,
    {},
    {
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
    },
  );
  return response.data;
};
